import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchSharedProject, API_BASE } from '../services/api';
import SharedPreviewView from '../components/workspace/SharedPreviewView';

/**
 * Extract Sandpack files JSON from the preview HTML response.
 * The backend embeds files as: const files = {...};
 */
function extractFilesFromPreviewHtml(html) {
  try {
    const startMarker = 'const files = ';
    const startIdx = html.indexOf(startMarker);
    if (startIdx === -1) return null;
    const jsonStart = startIdx + startMarker.length;
    // Find the matching end by looking for the await after the JSON
    const awaitIdx = html.indexOf('await loadSandpackClient', jsonStart);
    if (awaitIdx === -1) return null;
    // Walk backwards from await to find the semicolon that ends the JSON
    let jsonEnd = html.lastIndexOf(';', awaitIdx);
    if (jsonEnd <= jsonStart) return null;
    const jsonStr = html.substring(jsonStart, jsonEnd).trim();
    if (jsonStr) {
      const parsed = JSON.parse(jsonStr);
      // Convert from Sandpack format {"/App.js": {"code": "..."}} to flat {"/App.js": "..."}
      const flat = {};
      for (const [path, val] of Object.entries(parsed)) {
        flat[path] = typeof val === 'string' ? val : (val.code || '');
      }
      return flat;
    }
  } catch (e) {
    console.warn('[SharedProjectPage] Failed to extract files from preview HTML:', e);
  }
  return null;
}

async function fetchPreviewFiles(token) {
  // Try the /files endpoint first (new)
  try {
    const res = await fetch(`${API_BASE}/api/shared/${encodeURIComponent(token)}/files`);
    if (res.ok) {
      const data = await res.json();
      if (data.files && Object.keys(data.files).length > 0) {
        return data.files;
      }
    }
  } catch (e) { /* fall through */ }

  // Fallback: fetch the /preview HTML and extract embedded files JSON
  try {
    const res = await fetch(`${API_BASE}/api/shared/${encodeURIComponent(token)}/preview`);
    if (res.ok) {
      const html = await res.text();
      const files = extractFilesFromPreviewHtml(html);
      if (files && Object.keys(files).length > 0) {
        return files;
      }
    }
  } catch (e) { /* fall through */ }

  return {};
}

export default function SharedProjectPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let mounted = true;
    setState({ status: 'loading' });

    fetchSharedProject(token)
      .then((meta) => {
        if (!mounted) return;
        fetchPreviewFiles(token).then((files) => {
          if (!mounted) return;
          setState({
            status: 'ready',
            project: meta.project,
            files: files || {},
            previewUrl: meta.previewUrl,
            previewReady: Boolean(meta.previewReady),
          });
        });
      })
      .catch((e) => {
        if (!mounted) return;
        const unavailable = e.message?.toLowerCase().includes('not found')
          || e.code === 'NOT_FOUND';
        setState({
          status: unavailable ? 'unavailable' : 'error',
          message: e.message,
        });
      });

    return () => { mounted = false; };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div style={s.center}>
        <p style={s.text}>Loading shared project…</p>
      </div>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <div style={s.center}>
        <div style={s.card}>
          <h1 style={s.title}>This shared project is unavailable.</h1>
          <Link to="/" style={s.link}>Back to AI Code Agent</Link>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={s.center}>
        <div style={s.card}>
          <h1 style={s.title}>Something went wrong</h1>
          <p style={s.text}>{state.message || 'Could not load this shared project.'}</p>
          <Link to="/" style={s.link}>Back to AI Code Agent</Link>
        </div>
      </div>
    );
  }

  return (
    <SharedPreviewView
      project={state.project}
      files={state.files}
      previewUrl={state.previewUrl}
      previewReady={state.previewReady}
      shareToken={token}
    />
  );
}

const s = {
  center: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: 'var(--bg-primary)',
  },
  card: {
    maxWidth: 420,
    textAlign: 'center',
    padding: '32px 28px',
    borderRadius: 16,
    border: '1px solid var(--border)',
    background: 'var(--bg-secondary)',
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 20px',
  },
  text: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    margin: '0 0 20px',
    lineHeight: 1.55,
  },
  link: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--accent-light)',
    textDecoration: 'none',
  },
};
